/**
 * Tools → Neighbour Letters: the four master letters, and everything about
 * how a mailing is built.
 *
 * A signed replacement and a finished roof each build a packet — the right
 * letter filled in, twenty address labels and a cover sheet — and email it to
 * whoever prints and mails (ADR 0149). This screen is every dial on that: the
 * four master documents, who they are sent to, whose Google account fills them
 * in, which label stock the printer holds, and whether the address lookup is
 * switched on at all.
 *
 * The links live here rather than in the code because Jonathon reworded these
 * letters twice on the day they were built. Pointing the app at a different
 * document is a paste, not a deploy.
 *
 * The address lookup starts off, and the Try it button below is why: Josh
 * asked for RentCast to be tried against real local jobs before it was
 * switched on. Pressing it costs one lookup, sends nothing, and shows exactly
 * the twenty addresses a mailing would have used.
 *
 * Its own section rather than a corner of Integrations: "which letter goes to
 * the neighbours" is not a fact about an integration, and somebody looking for
 * it would never think to open Mercury.
 */

import { useCallback, useEffect, useState } from "react";

import {
  LABEL_STOCKS,
  NEIGHBOUR_PLACEHOLDERS,
  wholeNumberSetting,
} from "@roof-hawk/core";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import {
  fetchNeighbourLookupCount,
  pilotNeighbourLookup,
} from "../../lib/queries";
import { supabase } from "../../lib/supabase";
import { useSession } from "../../session";
import { PilotResult } from "./NeighbourPilotResult";

/** The columns this screen owns, and what each one is called on it. */
const LINKS = [
  [
    "install_notice_retail_url",
    "Install Notice — retail",
    "Sent when a retail replacement is signed.",
  ],
  [
    "install_notice_storm_url",
    "Install Notice — insurance",
    "The same letter, storm wording, for an insurance job.",
  ],
  [
    "work_complete_retail_url",
    "We Did Your Neighbor's Roof — retail",
    "Sent when the roof work order is marked complete.",
  ],
  [
    "work_complete_storm_url",
    "We Did Your Neighbor's Roof — insurance",
    "The same letter, storm wording, for an insurance job.",
  ],
] as const;

type Settings = {
  neighbour_letters_enabled: boolean | null;
  neighbour_letters_to: string | null;
  install_notice_retail_url: string | null;
  install_notice_storm_url: string | null;
  work_complete_retail_url: string | null;
  work_complete_storm_url: string | null;
  neighbour_address_lookup_enabled: boolean | null;
  neighbour_address_monthly_limit: number | null;
  neighbour_label_stock: string | null;
  neighbour_letters_reminder_days: number | null;
  neighbour_letters_docs_profile_id: string | null;
};

const EMPTY: Settings = {
  neighbour_letters_enabled: true,
  neighbour_letters_to: "",
  install_notice_retail_url: "",
  install_notice_storm_url: "",
  work_complete_retail_url: "",
  work_complete_storm_url: "",
  neighbour_address_lookup_enabled: false,
  neighbour_address_monthly_limit: 300,
  neighbour_label_stock: "avery_5160",
  neighbour_letters_reminder_days: 3,
  neighbour_letters_docs_profile_id: null,
};

const PLACEHOLDER_LIST = NEIGHBOUR_PLACEHOLDERS.map((n) => `{{${n}}}`).join(
  ", ",
);

const COLUMNS =
  "neighbour_letters_enabled, neighbour_letters_to, " +
  "install_notice_retail_url, install_notice_storm_url, " +
  "work_complete_retail_url, work_complete_storm_url, " +
  "neighbour_address_lookup_enabled, neighbour_address_monthly_limit, " +
  "neighbour_label_stock, neighbour_letters_reminder_days, " +
  "neighbour_letters_docs_profile_id";

export function NeighbourLettersTool() {
  const { isAdmin, people } = useSession();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [usedThisMonth, setUsedThisMonth] = useState<number | null>(null);
  const [pilotJob, setPilotJob] = useState("");
  const [pilotBusy, setPilotBusy] = useState(false);
  const [pilotResult, setPilotResult] = useState<Awaited<
    ReturnType<typeof pilotNeighbourLookup>
  > | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const { data, error: loadError } = await supabase
        .from("company_settings")
        .select(COLUMNS)
        .eq("id", 1)
        .maybeSingle();
      if (!alive) return;
      if (loadError) setError(loadError.message);
      else setSettings({ ...EMPTY, ...((data as Settings | null) ?? {}) });
      const used = await fetchNeighbourLookupCount().catch(() => null);
      if (alive) setUsedThisMonth(used);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(async (patch: Partial<Settings>) => {
    setBusy(true);
    setError(null);
    setNote(null);
    const { error: saveError } = await supabase
      .from("company_settings")
      .update(patch)
      .eq("id", 1);
    setBusy(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setSettings((current) => (current ? { ...current, ...patch } : current));
    setNote(t("Saved."));
  }, []);

  /**
   * Naming whoever fills the letters in, and moving the permission with them.
   *
   * The setting says who does it and the profile marker is what makes their
   * next Google connection ask for Docs and Drive; leaving the marker on the
   * person who used to do it would keep a permission nobody needs on an
   * account that is not using it. All of that is one database function that
   * checks the caller is an admin (review item 6 on PR #257) — three
   * unchecked writes from here could leave the setting naming somebody whose
   * connection never asks, and every packet failing until it blocks.
   */
  const chooseMerger = useCallback(async (profileId: string | null) => {
    setBusy(true);
    setError(null);
    setNote(null);
    const { error: saveError } = await supabase.rpc(
      "set_neighbour_letters_merger",
      { p_profile_id: profileId },
    );
    setBusy(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setSettings((current) =>
      current
        ? { ...current, neighbour_letters_docs_profile_id: profileId }
        : current,
    );
    setNote(
      profileId
        ? t(
            "Saved. They need to reconnect Google under Integrations before the next mailing.",
          )
        : t("Saved."),
    );
  }, []);

  /** One lookup against one real job, which sends nothing. */
  const tryLookup = useCallback(async () => {
    setPilotBusy(true);
    setPilotResult(null);
    setError(null);
    const { data, error: lookupError } = await supabase
      .from("jobs")
      .select("id")
      .eq("job_number", Number(pilotJob.trim()))
      .maybeSingle();
    if (lookupError || !data) {
      setPilotBusy(false);
      setError(
        lookupError?.message ??
          t("No job {number}.", { number: pilotJob.trim() }),
      );
      return;
    }
    const result = await pilotNeighbourLookup(String(data.id));
    setPilotResult(result);
    setPilotBusy(false);
    const used = await fetchNeighbourLookupCount().catch(() => null);
    setUsedThisMonth(used);
  }, [pilotJob]);

  if (!isAdmin) {
    return <div className="muted">{t("Only an admin can change these.")}</div>;
  }

  if (!settings) return <div className="muted">{t("Loading…")}</div>;

  return (
    <div className="block">
      {error && <ErrorNote error={error} />}
      {note && <div className="muted">{note}</div>}

      <div className="row">
        <label>
          <input
            type="checkbox"
            disabled={busy}
            checked={settings.neighbour_letters_enabled !== false}
            onChange={(e) =>
              void save({ neighbour_letters_enabled: e.target.checked })
            }
          />{" "}
          {t("Send neighbour letters")}
        </label>
      </div>
      <div className="muted">
        {t(
          "When a replacement is signed, and again when its roof is finished, the person below is emailed a link to the right letter and the details to type into it. Repairs are never included. Turning this off leaves any letters already owed waiting rather than throwing them away.",
        )}
      </div>

      <div className="row">
        <label>
          {t("Who prints and mails them")}
          <input
            type="email"
            disabled={busy}
            placeholder="kelsey@copperridge-roofing.com"
            defaultValue={settings.neighbour_letters_to ?? ""}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== (settings.neighbour_letters_to ?? "").trim())
                void save({ neighbour_letters_to: value || null });
            }}
          />
        </label>
      </div>

      {LINKS.map(([column, label, hint]) => (
        <div key={column} className="row">
          <label>
            {label}
            <input
              type="url"
              disabled={busy}
              placeholder="https://docs.google.com/document/d/…"
              defaultValue={settings[column] ?? ""}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value !== (settings[column] ?? "").trim())
                  void save({ [column]: value || null } as Partial<Settings>);
              }}
            />
          </label>
          <div className="muted">{t(hint)}</div>
        </div>
      ))}

      <div className="muted">
        {t(
          "These are the master letters. Nothing is ever written to them: a temporary copy is filled in, saved as a PDF and deleted. Each master needs the placeholders {placeholders} where its wording wants them — a placeholder nobody filled in stops the mailing rather than posting a letter with a gap in it. The install notice is the only one that names the customer.",
          { placeholders: PLACEHOLDER_LIST },
        )}
      </div>

      <h3>{t("Filling them in")}</h3>

      <div className="row">
        <label>
          {t("Whose Google account opens the letters")}
          <select
            disabled={busy}
            value={settings.neighbour_letters_docs_profile_id ?? ""}
            onChange={(e) => void chooseMerger(e.target.value || null)}
          >
            <option value="">{t("Nobody yet")}</option>
            {people
              .filter((person) => person.active !== false)
              .map((person) => (
                <option key={person.id} value={person.id}>
                  {person.full_name}
                </option>
              ))}
          </select>
        </label>
      </div>
      <div className="muted">
        {t(
          "This person’s Google connection copies the master and fills it in, so the letters have to live in — or be shared with — their Drive. They are the only person asked for the Docs and Drive permissions, and they must reconnect Google under Integrations after being named here, or every mailing stops with that reason on it.",
        )}
      </div>

      <h3>{t("Finding the neighbours")}</h3>

      <div className="row">
        <label>
          <input
            type="checkbox"
            disabled={busy}
            checked={settings.neighbour_address_lookup_enabled === true}
            onChange={(e) =>
              void save({ neighbour_address_lookup_enabled: e.target.checked })
            }
          />{" "}
          {t("Look up the neighbouring addresses automatically")}
        </label>
      </div>
      <div className="muted">
        {t(
          "Off until somebody has tried it on real local jobs — use the button below. While it is off, every mailing waits with that reason on it rather than posting a list nobody has read.",
        )}
      </div>

      <div className="row">
        <label>
          {t("Most lookups a month")}
          <input
            type="number"
            min={0}
            disabled={busy}
            defaultValue={settings.neighbour_address_monthly_limit ?? 300}
            onBlur={(e) => {
              // Blank saves nothing: a zero here stops every lookup.
              const value = wholeNumberSetting(e.target.value);
              if (
                value !== null &&
                value !== settings.neighbour_address_monthly_limit
              )
                void save({ neighbour_address_monthly_limit: value });
            }}
          />
        </label>
        <div className="muted">
          {usedThisMonth === null
            ? t("One or two per job is normal.")
            : t(
                "{n} used this month. One or two per job is normal — the second letter reuses the first one's addresses.",
                { n: usedThisMonth },
              )}
        </div>
      </div>

      <div className="row">
        <label>
          {t("Job number to try")}
          <input
            type="text"
            inputMode="numeric"
            disabled={pilotBusy}
            value={pilotJob}
            placeholder="1042"
            onChange={(e) => setPilotJob(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="ghost"
          disabled={pilotBusy || pilotJob.trim() === ""}
          onClick={() => void tryLookup()}
        >
          {pilotBusy ? t("Looking…") : t("Try a lookup")}
        </button>
        <div className="muted">
          {t(
            "Costs one lookup and sends nothing. It shows the addresses a mailing for that job would have used.",
          )}
        </div>
      </div>

      {pilotResult && <PilotResult result={pilotResult} />}

      <h3>{t("Printing and posting")}</h3>

      <div className="row">
        <label>
          {t("Label stock")}
          <select
            disabled={busy}
            value={settings.neighbour_label_stock ?? "avery_5160"}
            onChange={(e) =>
              void save({ neighbour_label_stock: e.target.value })
            }
          >
            {Object.values(LABEL_STOCKS).map((stock) => (
              <option key={stock.key} value={stock.key}>
                {stock.name}
              </option>
            ))}
          </select>
        </label>
        <div className="muted">
          {t(
            "What is in the printer. The labels are drawn to fit this exactly, so a change here is a change to where they land on the sheet.",
          )}
        </div>
      </div>

      <div className="row">
        <label>
          {t("Remind after (days)")}
          <input
            type="number"
            min={0}
            disabled={busy}
            defaultValue={settings.neighbour_letters_reminder_days ?? 3}
            onBlur={(e) => {
              // Blank saves nothing: a zero here stops the reminders.
              const value = wholeNumberSetting(e.target.value);
              if (
                value !== null &&
                value !== settings.neighbour_letters_reminder_days
              )
                void save({ neighbour_letters_reminder_days: value });
            }}
          />
        </label>
        <div className="muted">
          {t(
            "How long a packet may sit before whoever prints it is nudged. Zero stops the nudges. What is waiting is always on Marketing → Neighbour letters.",
          )}
        </div>
      </div>
    </div>
  );
}
